import { useCallback, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import "./geofence.css";
import Icon from "./Icon";

// India-ish default view when there's no existing polygon to frame.
const DEFAULT_CENTER = [20.5937, 78.9629];
const DEFAULT_ZOOM = 5;

// Photon (photon.komoot.io) is a free, key-less OSM geocoder built for
// search-as-you-type — unlike Nominatim, whose usage policy forbids
// autocomplete against the public server.
const PHOTON_URL = "https://photon.komoot.io/api/";

// Custom "you are here" pin — an SVG divIcon (no external image, no Leaflet
// default-marker path issues). An indigo teardrop with the brand's yellow sun
// (lifted straight from logo-mark.svg) centred in the head, anchored at its
// tip. Styled in geofence.css (drop shadow + drop-in animation).
// The <g> transform maps the sun's bbox (centre ~647.5,1037.85 in the logo's
// coordinate space) into the pin head at (12, 9.5).
const SUN_PATH =
  "M563.22,1096.2c.91-1.07,1.85-2.04,2.82-2.84,4-3.31,16.64-10.97,29.74,16.93,2.81,5.99,5.38,13.08,7.67,20.62,4.95,2.38,10,4.35,15.11,5.87.03-1.23.12-2.41.32-3.51.94-5.1,5.7-19.1,33.67-6.14,6.03,2.79,12.58,6.59,19.21,10.92,5.26-1.29,10.41-2.99,15.4-5.1-.82-1.01-1.56-2.04-2.14-3.06-2.56-4.51-7.91-18.29,21.84-26.35,6.69-1.81,14.51-3.15,22.75-4.11,2.85-3.89,5.47-8.01,7.8-12.39.2-.37.37-.75.56-1.12-1.61-.26-3.11-.64-4.46-1.14-4.86-1.81-17.82-8.93-.21-34.22,4.19-6.01,9.68-12.43,15.77-18.8-.38-5.16-1.16-10.28-2.31-15.3-1.73,1.11-3.43,2.01-5.1,2.6-4.89,1.74-19.39,4.62-22.15-26.08-.69-7.63-.57-16.53.08-25.75-3.53-3.53-7.34-6.82-11.44-9.85-.65,2.3-1.46,4.36-2.48,6.1-2.63,4.48-11.89,16-33.74-5.74-5.5-5.47-11.2-12.47-16.7-20.06-4.99-.37-9.98-.38-14.93-.02,1,2.22,1.72,4.36,2.06,6.37.86,5.11,1.18,19.9-29.53,17.29-7.57-.64-16.22-2.28-25.12-4.49-4.04,2.93-7.88,6.17-11.49,9.72,1.97.99,3.72,2.07,5.15,3.29,3.95,3.36,13.69,14.48-11.51,32.23-5.92,4.17-13.18,8.28-20.97,12.14-1.27,5.02-2.16,10.08-2.65,15.14,1.75-.36,3.43-.55,4.99-.54,5.19.04,19.8,2.29,11.9,32.09-1.76,6.63-4.47,14-7.72,21.53,2.23,4.77,4.86,9.37,7.82,13.77Z";

const ME_ICON = L.divIcon({
  className: "geo-me-marker",
  html:
    '<span class="geo-me-pin">' +
    '<svg width="42" height="42" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M12 22s7-6.7 7-12.5A7 7 0 0 0 5 9.5C5 15.3 12 22 12 22z" fill="#4f46e5" stroke="#ffffff" stroke-width="1.4" stroke-linejoin="round"/>' +
    '<g transform="translate(12 9.5) scale(0.049) translate(-647.5 -1037.85)">' +
    '<path fill="#f5ea14" d="' + SUN_PATH + '"/>' +
    "</g></svg></span>",
  iconSize: [42, 42],
  iconAnchor: [21, 39], // tip of the teardrop
  tooltipAnchor: [0, -38],
});

/** Human label for a Photon feature — "name, city, state, country" (deduped). */
function photonLabel(p = {}) {
  return [p.name, p.city || p.district || p.county, p.state, p.country]
    .filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i)
    .join(", ");
}

/**
 * Imperative Leaflet + Geoman wrapper for drawing ONE polygon geofence.
 *
 * - `value`: array of { lat, lng } vertices — used to seed the map once on mount
 *   (the component is uncontrolled thereafter, the Leaflet way).
 * - `onChange(points)`: fires whenever the polygon is drawn, edited, or removed.
 * - `readOnly`: render the polygon without the drawing toolbar.
 *
 * Uses OpenStreetMap tiles (no API key) and Photon autocomplete for optional
 * place search to recenter the map.
 */
export default function GeofenceMap({ value, onChange, readOnly = false, height = 360 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const polygonRef = useRef(null);
  const meRef = useRef(null); // "you are here" marker
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [searchQ, setSearchQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState("");
  const [locating, setLocating] = useState(false);
  const pickedRef = useRef(false); // suppress a re-query right after a pick

  // Propagate the current polygon's vertices up whenever it's edited/dragged.
  const emitChange = useCallback(() => {
    const layer = polygonRef.current;
    if (!layer) {
      onChangeRef.current?.([]);
      return;
    }
    const ring = layer.getLatLngs()[0] || [];
    onChangeRef.current?.(ring.map((p) => ({ lat: p.lat, lng: p.lng })));
  }, []);

  const attachLayer = useCallback((layer) => {
    polygonRef.current = layer;
    layer.on("pm:edit", emitChange);
    layer.on("pm:update", emitChange);
    layer.on("pm:dragend", emitChange);
  }, [emitChange]);

  // Draw the saved polygon and zoom to it — but only when nothing is drawn yet,
  // so it seeds the fence on open (the edit drawer fills `value` a tick after
  // mount) without ever fighting the admin's later edits.
  const seedPolygon = useCallback((pts) => {
    const map = mapRef.current;
    if (!map || polygonRef.current || !Array.isArray(pts) || pts.length < 3) return;
    const layer = L.polygon(pts.map((p) => [p.lat, p.lng]), { color: "#4f46e5" }).addTo(map);
    attachLayer(layer);
    // Recompute size first — the drawer may still be mid-open, which would
    // otherwise make fitBounds pick the wrong zoom.
    map.invalidateSize();
    map.fitBounds(layer.getBounds(), { padding: [24, 24] });
  }, [attachLayer]);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = L.map(containerRef.current, { zoomControl: true }).setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);

    if (!readOnly) {
      map.pm.addControls({
        position: "topright",
        drawPolygon: true,
        editMode: true,
        removalMode: true,
        dragMode: true,
        // Everything else off — a fence is a single polygon.
        drawMarker: false,
        drawCircle: false,
        drawCircleMarker: false,
        drawPolyline: false,
        drawRectangle: false,
        drawText: false,
        cutPolygon: false,
        rotateMode: false,
      });
      map.pm.setGlobalOptions({ pathOptions: { color: "#4f46e5" } });

      map.on("pm:create", (e) => {
        // Only one fence polygon — replace any previous shape.
        if (polygonRef.current && polygonRef.current !== e.layer) {
          polygonRef.current.remove();
        }
        attachLayer(e.layer);
        emitChange();
      });

      map.on("pm:remove", (e) => {
        if (e.layer === polygonRef.current) {
          polygonRef.current = null;
          emitChange();
        }
      });
    }

    // Leaflet needs a size recalculation once the container has laid out —
    // repeat across the drawer's open transition so tiles fill correctly.
    const resizes = [0, 250, 600].map((d) => setTimeout(() => map.invalidateSize(), d));

    return () => {
      resizes.forEach(clearTimeout);
      map.remove();
      mapRef.current = null;
      polygonRef.current = null;
    };
    // Mount-only: seeding is handled by the value effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed / zoom to the saved polygon once `value` is available. Runs on mount
  // and again when the edit drawer populates the points a tick later.
  useEffect(() => {
    seedPolygon(value);
  }, [value, seedPolygon]);

  // Drop / move the "you are here" marker and optionally recenter on it.
  // `auto` is the on-open call: it stays silent on errors and won't recenter
  // away from a saved polygon that's already framed.
  const locate = useCallback((auto = false) => {
    const map = mapRef.current;
    if (!map) return;
    if (!navigator.geolocation) {
      if (!auto) setSearchErr("Location isn't available on this device.");
      return;
    }
    setLocating(true);
    if (!auto) setSearchErr("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const ll = [pos.coords.latitude, pos.coords.longitude];
        if (meRef.current) {
          meRef.current.setLatLng(ll);
        } else {
          meRef.current = L.marker(ll, { icon: ME_ICON, keyboard: false })
            .addTo(map)
            .bindTooltip("You are here", { direction: "top" });
        }
        // Button always recenters; the on-open call keeps a saved fence framed.
        if (!auto || !polygonRef.current) map.setView(ll, 17);
        setLocating(false);
      },
      () => {
        if (!auto) setSearchErr("Couldn't get your location — allow location access and try again.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
    );
  }, []);

  // Show the admin's location automatically when the map opens. Slight delay so
  // a saved polygon (edit mode) is seeded first — then we won't recenter off it.
  useEffect(() => {
    if (readOnly) return;
    const t = setTimeout(() => locate(true), 250);
    return () => clearTimeout(t);
  }, [readOnly, locate]);

  // Debounced Photon autocomplete: fetch suggestions ~300ms after typing stops.
  useEffect(() => {
    if (pickedRef.current) {
      // The query changed because we just filled it from a pick — don't re-query.
      pickedRef.current = false;
      return;
    }
    const q = searchQ.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setSearchErr("");
      setSearching(false);
      return;
    }
    let ignore = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${PHOTON_URL}?q=${encodeURIComponent(q)}&limit=5`);
        const json = await res.json();
        if (ignore) return;
        const items = (json.features || [])
          .filter((f) => f?.geometry?.coordinates?.length === 2)
          .map((f) => ({
            lat: f.geometry.coordinates[1],
            lng: f.geometry.coordinates[0],
            label: photonLabel(f.properties),
          }))
          .filter((s) => s.label);
        setSuggestions(items);
        setSearchErr(items.length ? "" : "No matches.");
      } catch {
        if (!ignore) setSearchErr("Search failed — check your connection.");
      } finally {
        if (!ignore) setSearching(false);
      }
    }, 300);
    return () => {
      ignore = true;
      clearTimeout(t);
    };
  }, [searchQ]);

  const pickSuggestion = (s) => {
    if (!mapRef.current) return;
    mapRef.current.setView([s.lat, s.lng], 16);
    pickedRef.current = true;
    setSearchQ(s.label);
    setSuggestions([]);
    setSearchErr("");
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    if (suggestions[0]) pickSuggestion(suggestions[0]);
  };

  return (
    <div className="geo-map-wrap">
      {!readOnly && (
        <form className="geo-map-search" onSubmit={onSearchSubmit} autoComplete="off">
          <div className="geo-autocomplete">
            <Icon name="search" size={13} className="geo-autocomplete-icon" />
            <input
              className="input"
              style={{ paddingLeft: 30 }}
              placeholder="Search a place to centre the map…"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onBlur={() => setTimeout(() => setSuggestions([]), 120)}
            />
            {searching && searchQ.trim().length >= 3 && <span className="geo-autocomplete-spin">…</span>}
            {suggestions.length > 0 && (
              <ul className="geo-suggest">
                {suggestions.map((s, i) => (
                  // onMouseDown fires before the input's blur, so the pick isn't lost.
                  <li key={i} onMouseDown={() => pickSuggestion(s)}>
                    <Icon name="map-pin" size={13} />
                    <span>{s.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => locate(false)} disabled={locating} title="Centre the map on my current location">
            <Icon name="crosshair" size={14} /> {locating ? "…" : "My location"}
          </button>
        </form>
      )}
      <div ref={containerRef} className="geo-map" style={{ height }} />
      {!readOnly && (
        <div className={`geo-map-hint ${searchErr ? "warn" : ""}`}>
          {searchErr || "Use the polygon tool (top-right) to draw the area, then drag vertices to fine-tune."}
        </div>
      )}
    </div>
  );
}
